import { Breadcrumb } from "antd";
import {
  breadcrumbHomeItem,
  breadcrumbItem,
  CustomTable,
  CustomTextField,
  TableHeaderBar,
  TableHeaderInfo,
} from "~/components";
import { LeaseListPageLoaderType } from "./loaders";
import { useLoaderData } from "react-router-dom";
import { useRouteActivity } from "~/hooks/useRouteActivity";
import { ColumnsType } from "antd/es/table";
import { FormattedLeaseData } from "~/types";
import { CalendarOutlined, UserOutlined } from "@ant-design/icons";
import {
  ApplicationStatusTag,
  LeaseStatusTag,
  LeaseTypeTag,
} from "~/components/Tags";
import { useAppTranslation } from "~/hooks/useAppTranslation";
import i18n from "~/i18n";
import { compareIsoDates, LEASE_TYPE_ORDER } from "~/utils";

export const getLeaseTableColumns = (): ColumnsType<FormattedLeaseData> => [
  {
    title: i18n.t("Lease_ID"),
    dataIndex: ["leaseInfo", "id"],
    key: "leaseId",
    width: 250,
    render: (_, record: FormattedLeaseData) => {
      const id = record.leaseInfo.id;
      return <a href={`/lease/${id}`}>{id}</a>;
    },
  },
  {
    title: i18n.t("Lease_Status"),
    dataIndex: ["leaseInfo", "status"],
    key: "leaseStatus",
    width: 250,
    sorter: (a, b) => a.leaseInfo.status.localeCompare(b.leaseInfo.status),
    defaultSortOrder: "ascend",
    render: (_, record: FormattedLeaseData) => {
      const status = record.leaseInfo?.status;
      return <LeaseStatusTag status={status} />;
    },
  },
  {
    title: "Application Status", // TODO: [Translate]
    dataIndex: ["applicationInfo", "status"],
    key: "applicationStatus",
    width: 250,
    render: (_, record: FormattedLeaseData) => {
      const status = record.application?.status;
      return <ApplicationStatusTag status={status} />;
    },
  },
  {
    title: i18n.t("Type"),
    dataIndex: ["leaseInfo", "type"],
    key: "leaseType",
    width: 250,
    sorter: (a, b) => {
      const typeA = a.leaseInfo?.type;
      const typeB = b.leaseInfo?.type;
      if (!typeA || !typeB) return 0;
      return (LEASE_TYPE_ORDER[typeA] || 0) - (LEASE_TYPE_ORDER[typeB] || 0);
    },
    render: (_, record: FormattedLeaseData) => {
      const type = record.leaseInfo?.type;
      return <LeaseTypeTag type={type} />;
    },
  },
  {
    title: i18n.t("Start_Date"),
    dataIndex: ["leaseInfo", "startDate"],
    key: "startDate",
    width: 250,
    sorter: (a, b) =>
      compareIsoDates(a.leaseInfo?.startDate, b.leaseInfo?.startDate),
    render: (_, record: FormattedLeaseData) => {
      const startDate = record.leaseInfo?.startDate;
      return (
        <CustomTextField
          value={startDate}
          onEmpty="none"
          prefix={<CalendarOutlined />}
        />
      );
    },
  },
  {
    title: i18n.t("Date_Of_Availability"),
    dataIndex: ["leaseInfo", "markedDate"],
    key: "markedDate",
    width: 250,
    sorter: (a, b) =>
      compareIsoDates(a.leaseInfo?.markedDate, b.leaseInfo?.markedDate),
    render: (_, record: FormattedLeaseData) => {
      const markedDate = record.leaseInfo?.markedDate;
      return (
        <CustomTextField
          value={markedDate}
          onEmpty="none"
          prefix={<CalendarOutlined />}
        />
      );
    },
  },
  {
    title: i18n.t("End_Date"),
    dataIndex: ["leaseInfo", "endDate"],
    key: "endDate",
    width: 250,
    sorter: (a, b) =>
      compareIsoDates(a.leaseInfo?.endDate, b.leaseInfo?.endDate),
    render: (_, record: FormattedLeaseData) => {
      const endDate = record.leaseInfo?.endDate;
      return (
        <CustomTextField
          value={endDate}
          onEmpty="none"
          prefix={<CalendarOutlined />}
        />
      );
    },
  },
  {
    title: "Company Name",
    dataIndex: ["managerInfo", "companyName"],
    key: "companyName",
    width: 250,
    sorter: (a, b) =>
      (a.managerInfo?.companyName || "").localeCompare(
        b.managerInfo?.companyName || "",
      ),
    render: (_, record: FormattedLeaseData) => {
      const companyName = record.managerInfo?.companyName;
      return <CustomTextField value={companyName} prefix={<UserOutlined />} />;
    },
  },
  {
    title: i18n.t("Entity_Info"),
    dataIndex: ["entityDetails", "name"],
    key: "entityInfo",
    width: 250,
    sorter: (a, b) => {
      const valueA = `${a.entityType}: ${a.entityDetails?.name || ""}`;
      const valueB = `${b.entityType}: ${b.entityDetails?.name || ""}`;
      return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
    },
    render: (_, record: FormattedLeaseData) => {
      const id = record.entityDetails?.id;
      const type = record.entityType;
      const name = record.entityDetails?.name;
      return <a href={`/${type}/${id}`}>{name}</a>;
    },
  },
];

const leaseMobileDefaultSort = {
  dataIndex: ["leaseInfo", "status"] as string[],
  order: "ascend" as const,
};

export const LeaseListPage: React.FC = () => {
  const loaderData = useLoaderData() as LeaseListPageLoaderType;
  const { t } = useAppTranslation();
  const { isTableLoading } = useRouteActivity();
  const columns = getLeaseTableColumns();

  return (
    <>
      <Breadcrumb
        separator=">"
        items={[breadcrumbHomeItem, breadcrumbItem(t("Lease_Info"))]}
      />
      <section style={{ marginTop: "2rem" }}>
      <CustomTable
        tableHeaders={
          <TableHeaderBar
            left={
              <TableHeaderInfo
                title="Approved Leases"
                count={loaderData.leaseListApproved.length}
                countLabel={t("Total_Records_Found")}
              />
            }
          />
        }
        mobileCardConfig={{
          titleKey: "leaseId",
          primaryKeys: ["leaseStatus", "applicationStatus", "leaseType"],
        }}
        mobileDefaultSort={leaseMobileDefaultSort}
        tableProps={{
          dataSource: loaderData.leaseListApproved ?? [],
          columns,
          loading: isTableLoading,
        }}
      />
      <CustomTable
        tableHeaders={
          <TableHeaderBar
            left={
              <TableHeaderInfo
                title="Under Review Leases"
                count={loaderData.leaseListUnderReview.length}
                countLabel={t("Total_Records_Found")}
              />
            }
          />
        }
        mobileCardConfig={{
          titleKey: "leaseId",
          primaryKeys: ["leaseStatus", "applicationStatus", "leaseType"],
        }}
        mobileDefaultSort={leaseMobileDefaultSort}
        tableProps={{
          dataSource: loaderData.leaseListUnderReview ?? [],
          columns,
          loading: isTableLoading,
        }}
      />
      <CustomTable
        tableHeaders={
          <TableHeaderBar
            left={
              <TableHeaderInfo
                title="Submitted Leases"
                count={loaderData.leaseListSubmitted.length}
                countLabel={t("Total_Records_Found")}
              />
            }
          />
        }
        mobileCardConfig={{
          titleKey: "leaseId",
          primaryKeys: ["leaseStatus", "applicationStatus", "leaseType"],
        }}
        mobileDefaultSort={leaseMobileDefaultSort}
        tableProps={{
          dataSource: loaderData.leaseListSubmitted ?? [],
          columns,
          loading: isTableLoading,
        }}
      />
    </section>
    </>
  );
};
